import type { Database } from '../../database.types';

// Base types from database schema
export type Subscription = Database['public']['Tables']['subscriptions']['Row'];
export type Entitlement = Database['public']['Tables']['entitlements']['Row'];

// Tier and status types derived from database
export type SubscriptionTier = Entitlement['tier'];
export type SubscriptionStatus = Subscription['status'];
export type SubscriptionPlan = Subscription['plan'];

// Entitlement flags for feature gating
export interface EntitlementFlags {
  canViewImages: boolean;
  canViewVideos: boolean;
}

// The main entitlements context value
export interface EntitlementsContextValue {
  // Current user's tier
  tier: SubscriptionTier;
  // Credits remaining for consumable features
  credits: number;
  // Feature flags based on subscription
  flags: EntitlementFlags;
  // Subscription details
  subscription: Subscription | null;
  // Loading state
  isLoading: boolean;
  // Error state
  error: Error | null;
  // Refresh entitlements data
  refresh: () => Promise<void>;
}

// Default entitlements for unauthenticated or free users
export const DEFAULT_ENTITLEMENTS: Omit<EntitlementsContextValue, 'refresh'> = {
  tier: 'free',
  credits: 0,
  flags: {
    canViewImages: false,
    canViewVideos: false,
  },
  subscription: null,
  isLoading: false,
  error: null,
};

// Tier display labels (SOW-aligned + backward-compat for legacy tiers)
const TIER_LABELS_FALLBACK: Record<string, string> = {
  free: 'Free',
  premium: 'Premium',
  vip: 'VIP',
  // Legacy tiers (before SOW alignment)
  basic: 'Premium',
  pro: 'Premium',
  enterprise: 'VIP',
};

// Tier badge color classes (SOW-aligned + backward-compat for legacy tiers)
const TIER_COLORS_FALLBACK: Record<string, string> = {
  free: 'bg-gray-100 text-gray-700',
  premium: 'bg-purple-100 text-purple-700',
  vip: 'bg-amber-100 text-amber-700',
  // Legacy tiers
  basic: 'bg-purple-100 text-purple-700',
  pro: 'bg-purple-100 text-purple-700',
  enterprise: 'bg-amber-100 text-amber-700',
};

// Tier hierarchy for comparison (SOW-aligned + backward-compat for legacy tiers)
const TIER_ORDER_FALLBACK: Record<string, number> = {
  free: 0,
  premium: 1,
  vip: 2,
  // Legacy tiers
  basic: 1,
  pro: 1,
  enterprise: 2,
};

export const TIER_LABELS: Record<SubscriptionTier, string> = {
  free: TIER_LABELS_FALLBACK.free,
  premium: TIER_LABELS_FALLBACK.premium,
  vip: TIER_LABELS_FALLBACK.vip,
};

export const TIER_COLORS: Record<SubscriptionTier, string> = {
  free: TIER_COLORS_FALLBACK.free,
  premium: TIER_COLORS_FALLBACK.premium,
  vip: TIER_COLORS_FALLBACK.vip,
};

export const TIER_ORDER: Record<SubscriptionTier, number> = {
  free: TIER_ORDER_FALLBACK.free,
  premium: TIER_ORDER_FALLBACK.premium,
  vip: TIER_ORDER_FALLBACK.vip,
};

// Backward-compatible helpers (use these when tier values may come from legacy DB rows)
export function getTierLabel(tier: string): string {
  return TIER_LABELS_FALLBACK[tier] ?? 'Free';
}

export function getTierColor(tier: string): string {
  return TIER_COLORS_FALLBACK[tier] ?? TIER_COLORS_FALLBACK.free;
}

export function getTierOrder(tier: string): number {
  return TIER_ORDER_FALLBACK[tier] ?? 0;
}
